import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Calculator, Phone, Star } from "lucide-react";
import logoImage from "@/assets/lifetime-movers-logo-transparent.png";
import { supabase } from "@/integrations/supabase/client";

const SESSION_KEY = "lt_exit_offer_shown";
const DWELL_MS_MOBILE = 8000;
const DWELL_MS_DESKTOP = 10000;
const MOBILE_SCROLL_DEPTH = 0.4;

type Trigger = "dwell" | "exit_mouse" | "scroll_depth" | "scroll_up" | "tab_hidden";

const isMobileDevice = () =>
  typeof window !== "undefined" && window.matchMedia("(pointer: coarse)").matches;

const logEvent = (event_type: string, page_path: string, trigger_type?: Trigger) => {
  try {
    (window as any).dataLayer?.push({ event: `exit_offer_${event_type}`, page_path, trigger_type });
    void (supabase as any).from("exit_offer_events").insert({
      event_type,
      trigger_type: trigger_type ?? null,
      page_path: page_path.slice(0, 300),
      device: isMobileDevice() ? "mobile" : "desktop",
    }).then(() => {});
  } catch {}
};

// Public marketing pages only. Never interrupt the funnel, admin, or customer tools.
const EXCLUDED = [
  /^\/admin/, /^\/booking/, /^\/quote/, /^\/sign/, /^\/customer/, /^\/partner/,
  /^\/auth/, /^\/resume-booking/, /^\/unsubscribe/, /^\/view-estimate/, /^\/estimate\//,
  /^\/paperwork/, /^\/payment/, /^\/foreman/, /^\/inventory/, /^\/carrier/,
  /^\/sitemap\/?$/,
];

/**
 * Site-wide exit-intent offer: "Save 10–20%" → free instant estimate.
 * Once per session. Desktop: mouse exit or 10s dwell. Mobile: 8s dwell, 40% scroll,
 * fast scroll back up (heading for the address bar), or tab switch away.
 */
const ExitIntentOffer = () => {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const excluded = EXCLUDED.some((r) => r.test(pathname));

  useEffect(() => {
    if (excluded) return;
    try {
      if (sessionStorage.getItem(SESSION_KEY)) return;
    } catch {}

    let fired = false;
    const isTouch = isMobileDevice();
    const fire = (trigger: Trigger) => {
      if (fired) return;
      // Never interrupt someone actively filling out a form (e.g. homepage estimate form).
      const active = document.activeElement as HTMLElement | null;
      if (active && /^(INPUT|TEXTAREA|SELECT)$/.test(active.tagName)) return;
      try {
        if (sessionStorage.getItem(SESSION_KEY)) return;
        sessionStorage.setItem(SESSION_KEY, "1");
      } catch {}
      fired = true;
      setOpen(true);
      logEvent("shown", pathname, trigger);
    };

    const dwellTimer = window.setTimeout(() => fire("dwell"), isTouch ? DWELL_MS_MOBILE : DWELL_MS_DESKTOP);
    const onMouseOut = (e: MouseEvent) => {
      if (e.clientY <= 0 && !e.relatedTarget) fire("exit_mouse");
    };
    let lastY = window.scrollY;
    let lastT = Date.now();
    let maxY = lastY;
    const onScroll = () => {
      const y = window.scrollY;
      const now = Date.now();
      const h = document.documentElement.scrollHeight - window.innerHeight;
      if (h > 0 && y / h >= MOBILE_SCROLL_DEPTH) fire("scroll_depth");
      // Fast upward flick after reading a bit = likely heading to leave.
      const dt = now - lastT;
      if (maxY > 400 && dt > 0 && lastY - y > 0 && (lastY - y) / dt > 2.5) fire("scroll_up");
      maxY = Math.max(maxY, y);
      lastY = y;
      lastT = now;
    };
    const onVisibility = () => {
      // Shows when they come back to the tab.
      if (document.visibilityState === "hidden") hiddenAway = true;
      else if (hiddenAway) fire("tab_hidden");
    };
    let hiddenAway = false;
    document.addEventListener("mouseout", onMouseOut);
    if (isTouch) {
      window.addEventListener("scroll", onScroll, { passive: true });
      document.addEventListener("visibilitychange", onVisibility);
    }

    return () => {
      window.clearTimeout(dwellTimer);
      document.removeEventListener("mouseout", onMouseOut);
      window.removeEventListener("scroll", onScroll);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [pathname, excluded]);

  if (excluded) return null;

  const goEstimate = () => {
    logEvent("estimate_click", pathname);
    setOpen(false);
    navigate("/quote");
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-md border-t-4 border-t-secondary overflow-hidden">
        <DialogHeader>
          <img src={logoImage} alt="LifeTime Movers" className="h-14 w-auto mx-auto mb-2 object-contain" />
          <DialogTitle className="text-2xl font-bold text-center">
            Save <span className="text-secondary">10–20%</span> on your move
          </DialogTitle>
          <DialogDescription className="text-center text-base pt-2">
            Start with a free instant estimate, or call for personal service. Guaranteed quote, no broker fees, family owned.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-3 mt-4">
          <Button size="lg" className="text-lg bg-secondary text-secondary-foreground hover:bg-secondary/90" onClick={goEstimate}>
            <Calculator className="mr-2 h-5 w-5" />
            Get My Free Instant Estimate
          </Button>
          <Button asChild size="lg" variant="outline" className="text-lg">
            <a href="tel:6306869987" onClick={() => { logEvent("call_click", pathname); setOpen(false); }}>
              <Phone className="mr-2 h-5 w-5" />
              Call (630) 686-9987
            </a>
          </Button>
          <a
            href="/lifetime-movers-reviews"
            onClick={(e) => { e.preventDefault(); logEvent("reviews_click", pathname); setOpen(false); navigate("/lifetime-movers-reviews"); }}
            className="flex items-center justify-center gap-1 text-sm font-medium text-foreground hover:text-secondary mt-1"
          >
            <span className="flex text-secondary">
              {[0,1,2,3,4].map((i) => <Star key={i} className="h-4 w-4 fill-current" />)}
            </span>
            Rated 4.9 stars — read our reviews
          </a>
          <button
            type="button"
            onClick={() => { logEvent("dismiss", pathname); setOpen(false); }}
            className="text-sm text-muted-foreground hover:text-foreground"
          >
            No thanks
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ExitIntentOffer;
